import { Clipper, EndType, FillRule, JoinType } from '@countertype/clipper2-ts'
import type { PathsD } from '@countertype/clipper2-ts'
import type { PolygonPath, Space } from '@/model/types'
import { signedArea } from '@/lib/geometry'

/**
 * Auto-generate wall geometry from a level's spaces.
 *
 * Convention: a space's polygon is the *centerline* of its walls. Walls then merge
 * cleanly — a shared edge between two rooms yields ONE interior wall (not two), and the
 * building's outer boundary yields a uniform exterior wall.
 *
 * Method (all via polygon offset + boolean, using clipper2's double API):
 *   U          = union(all walled space polygons)              — the building footprint
 *   exterior   = offset(U, +ext/2) − offset(U, −ext/2)         — ring centered on U's boundary
 *   roomRings  = ∪ ( offset(room, +int/2) − offset(room, −int/2) )
 *   interior   = roomRings − exterior                          — shared internal walls only
 *
 * Single rooms get no interior walls (the int ring sits inside the ext ring and cancels).
 * Adjacent rooms share one int-thick wall. The result is watertight (exterior ∪ interior).
 */
export interface WallGeometry {
  exterior: PolygonPath[]
  interior: PolygonPath[]
}

const NZ = FillRule.NonZero
const PRECISION = 4 // decimal places ≈ 0.0001 ft resolution

function offset(paths: PathsD, delta: number): PathsD {
  if (delta === 0) return paths
  return Clipper.inflatePathsD(paths, delta, JoinType.Miter, EndType.Polygon, 2, PRECISION)
}

function difference(subject: PathsD, clip: PathsD): PathsD {
  if (subject.length === 0) return []
  if (clip.length === 0) return subject
  return Clipper.differenceD(subject, clip, NZ, PRECISION)
}

function union(a: PathsD, b?: PathsD): PathsD {
  if (b === undefined) return Clipper.unionD(a, NZ)
  if (a.length === 0) return b
  if (b.length === 0) return a
  return Clipper.unionD(a, b, NZ, PRECISION)
}

/** A centered ring of the given thickness around a set of closed paths. */
function ring(paths: PathsD, thickness: number): PathsD {
  const half = thickness / 2
  return difference(offset(paths, half), offset(paths, -half))
}

export function generateWalls(
  spaces: Space[],
  exteriorThickness: number,
  interiorThickness: number,
): WallGeometry {
  const walled = spaces.filter((s) => s.wallMode !== 'none' && s.polygon.length >= 3)
  if (walled.length === 0) return { exterior: [], interior: [] }

  const polys = walled.map((s) => s.polygon)
  const footprint = union(polys)
  let exterior = ring(footprint, exteriorThickness)

  let roomRings: PathsD = []
  for (const poly of polys) {
    roomRings = union(roomRings, ring([poly], interiorThickness))
  }
  let interior = difference(roomRings, exterior)

  // Open edges: bake full-edge cuts into BOTH bands so an open side has no wall at all (unlike a
  // window, which is a height-aware cut applied downstream). Removing it here means 2D and 3D both
  // pick it up for free. One space flagging a shared edge open is enough — it's a single wall.
  const openCuts = openEdgeCutPaths(walled, exteriorThickness)
  if (openCuts.length) {
    const clip = openCuts as unknown as PathsD
    exterior = difference(exterior, clip)
    interior = difference(interior, clip)
  }

  return { exterior, interior }
}

/**
 * Full-edge cut rectangle for an OPEN edge: spans the whole edge (minus a small end inset so the
 * adjacent walls keep their corners) × `perpThickness` across it, centered on the edge line so it
 * removes whatever wall sits there — interior-shared OR exterior. Mirrors `openingWorldRect` but
 * spanning the entire edge instead of a fixed opening width.
 */
export function openEdgeCutRect(
  polygon: PolygonPath,
  edgeIndex: number,
  perpThickness: number,
  endInset: number,
): PolygonPath | null {
  const n = polygon.length
  if (n < 2) return null
  const a = polygon[((edgeIndex % n) + n) % n]
  const b = polygon[(edgeIndex + 1) % n]
  const dx = b.x - a.x
  const dy = b.y - a.y
  const len = Math.hypot(dx, dy)
  if (len < 1e-9) return null
  const dir = { x: dx / len, y: dy / len }
  const nrm = { x: -dir.y, y: dir.x }
  // Clamp the inset so it never collapses/inverts the rectangle on short edges.
  let s0 = Math.min(endInset, len * 0.3)
  let s1 = len - s0
  if (s1 <= s0) {
    s0 = 0
    s1 = len
  }
  const hp = perpThickness / 2
  const e0 = { x: a.x + dir.x * s0, y: a.y + dir.y * s0 }
  const e1 = { x: a.x + dir.x * s1, y: a.y + dir.y * s1 }
  return [
    { x: e0.x - nrm.x * hp, y: e0.y - nrm.y * hp },
    { x: e1.x - nrm.x * hp, y: e1.y - nrm.y * hp },
    { x: e1.x + nrm.x * hp, y: e1.y + nrm.y * hp },
    { x: e0.x + nrm.x * hp, y: e0.y + nrm.y * hp },
  ]
}

/** Full-edge cut rectangles for every open edge across `walled` spaces. */
function openEdgeCutPaths(walled: Space[], exteriorThickness: number): PolygonPath[] {
  const perp = exteriorThickness * 1.5
  const inset = exteriorThickness * 0.5 // keep adjacent wall corners intact
  const rects: PolygonPath[] = []
  for (const sp of walled) {
    for (const ei of sp.openEdges ?? []) {
      const r = openEdgeCutRect(sp.polygon, ei, perp, inset)
      if (r) rects.push(r)
    }
  }
  return rects
}

/**
 * Subtract opening rectangles from generated wall geometry, for plan view only.
 * (3D extrudes the UNCUT walls and does height-aware cuts itself — see the Track-3 notes.)
 */
export function cutGeometry(geom: WallGeometry, cutters: PolygonPath[]): WallGeometry {
  if (cutters.length === 0) return geom
  const clip = cutters as unknown as PathsD
  return {
    exterior: difference(geom.exterior as unknown as PathsD, clip) as unknown as PolygonPath[],
    interior: difference(geom.interior as unknown as PathsD, clip) as unknown as PolygonPath[],
  }
}

/**
 * The centerline path the compound wall runs along: the plot boundary, inset inward by `inset`.
 * Used by BOTH the wall ring and the gate's edge anchoring, so the gate stays on the wall.
 */
export function compoundCenterline(boundary: PolygonPath, inset: number): PolygonPath {
  if (boundary.length < 3 || !inset) return boundary
  // Inward = shrink. Offset sign that shrinks depends on the boundary's winding.
  const shrink = signedArea(boundary) > 0 ? -inset : inset
  const res = offset([boundary] as unknown as PathsD, shrink)
  return ((res as unknown as PolygonPath[])[0]) ?? boundary
}

/** A centered ring of `thickness` along the compound-wall centerline. */
export function generateCompoundWall(centerline: PolygonPath, thickness: number): PolygonPath[] {
  if (centerline.length < 3) return []
  return ring([centerline] as unknown as PathsD, thickness) as unknown as PolygonPath[]
}
